import React, {useCallback, useMemo, useRef, useState} from "react";
import {usePage} from "@inertiajs/react";
import {Alert, Button, Form, Modal, Spinner} from "react-bootstrap";
import axios from "axios";
import {EditorContent, useEditor} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import HelpTooltip from "../HelpTooltip.jsx";

// Die wählbaren Systeme - Reihenfolge und Schlüssel wie im Backend
// (SupportTicketController::SYSTEMS). "ordermanagement" ist vorgewählt.
const SYSTEMS = [
    {key: "ordermanagement", label: "Ordermanagement"},
    {key: "weclapp", label: "Weclapp"},
    {key: "b2c-shop", label: "B2C-Shop"},
    {key: "b2b-shop", label: "B2B-Shop"},
    {key: "brand-website", label: "Brand-Website"},
    {key: "blog", label: "Blog"},
    {key: "newsletter", label: "Newsletter"},
    {key: "sonstiges", label: "Sonstiges"},
];

const TITEL = {
    problem: "Problem melden",
    vorschlag: "Verbesserungsvorschlag",
    hinweis: "Hinweis / Frage",
};

const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_MB = 8;

// Die Feldnamen bleiben allgemein (Betreff/Beschreibung) - die inhaltliche
// Führung steckt in den Platzhaltern, damit das Formular ruhig wirkt und
// trotzdem die richtigen Angaben einsammelt.
const COPY = {
    problem: {
        goalPlaceholder: "Was funktioniert nicht? z. B. Bestellung lässt sich nicht übertragen",
        detailsPlaceholder:
            "Was wolltest du tun, und was ist stattdessen passiert? " +
            "Fehlermeldungen gern als Screenshot anhängen.",
        referencePlaceholder: "Bestell-, Artikel- oder Kundennummer (optional)",
    },
    vorschlag: {
        goalPlaceholder: "Worum geht es? z. B. Lagerstatus auch in der Bestellansicht",
        detailsPlaceholder:
            "Wie läuft es heute, und wie wäre es besser?",
        referencePlaceholder: "Beispiel-Nummer (optional)",
    },
    hinweis: {
        goalPlaceholder: "Worum geht es? z. B. Frage zur Lagerübersicht",
        detailsPlaceholder:
            "Was möchtest du wissen oder mitteilen?",
        referencePlaceholder: "Bezug: Bestell- oder Artikelnummer (optional)",
    },
};

export default function SupportTicketModal({show, onHide}) {
    const {auth, supportTicketVisibilityOptions = {departments: [], meeting_user_label: "Meeting-Benutzer"}} = usePage().props;
    const departments = supportTicketVisibilityOptions.departments ?? [];

    const [type, setType] = useState(null);
    const [systems, setSystems] = useState(["ordermanagement"]);
    const [goal, setGoal] = useState("");
    const [reference, setReference] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [successId, setSuccessId] = useState(null);
    const [visibilityChoice, setVisibilityChoice] = useState("private");
    const [meetingVisible, setMeetingVisible] = useState(false);
    const fileInputRef = useRef(null);

    // Der Platzhalter des Editors hängt vom Ticket-Typ ab; die Extension wird
    // aber nur einmal konfiguriert. Deshalb liest sie über die Ref immer den
    // aktuellen Text.
    const placeholderRef = useRef(COPY.problem.detailsPlaceholder);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({openOnClick: false}),
            Placeholder.configure({placeholder: () => placeholderRef.current}),
        ],
        content: "",
    });

    const copy = type ? COPY[type] : null;
    if (copy) {
        placeholderRef.current = copy.detailsPlaceholder;
    }

    const reset = useCallback(() => {
        setType(null);
        setSystems(["ordermanagement"]);
        setGoal("");
        setReference("");
        setAttachments([]);
        setError(null);
        setSuccessId(null);
        setVisibilityChoice("private");
        setMeetingVisible(false);
        editor?.commands.clearContent();
    }, [editor]);

    const close = () => {
        onHide();
        // Nach einer Erfolgsmeldung beim nächsten Öffnen wieder frisch starten;
        // eine abgebrochene Eingabe bleibt dagegen erhalten.
        if (successId) {
            reset();
        }
    };

    const toggleSystem = (key) => {
        setSystems((prev) =>
            prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]
        );
    };

    const addFiles = useCallback((files) => {
        setError(null);
        setAttachments((prev) => {
            const next = [...prev];
            for (const file of files) {
                if (next.length >= MAX_ATTACHMENTS) {
                    setError(`Höchstens ${MAX_ATTACHMENTS} Screenshots pro Ticket.`);
                    break;
                }
                if (!/^image\/(png|jpe?g|gif|webp)$/.test(file.type)) {
                    setError("Nur Bilder (PNG, JPG, GIF, WebP) sind möglich.");
                    continue;
                }
                if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
                    setError(`"${file.name}" ist größer als ${MAX_ATTACHMENT_MB} MB.`);
                    continue;
                }
                next.push(file);
            }
            return next;
        });
    }, []);

    // Screenshots direkt aus der Zwischenablage - die niedrigste Hürde, um
    // ein Bild mitzuschicken.
    const handlePaste = useCallback(
        (e) => {
            const files = [...(e.clipboardData?.files || [])];
            if (files.length > 0) {
                addFiles(files);
            }
        },
        [addFiles]
    );

    const previews = useMemo(
        () => attachments.map((file) => ({file, url: URL.createObjectURL(file)})),
        [attachments]
    );

    const submit = async () => {
        if (sending) {
            return;
        }
        setError(null);

        const detailsHtml = editor?.getHTML() ?? "";
        const detailsText = editor?.getText() ?? "";

        if (!goal.trim()) {
            setError("Bitte einen Betreff eintragen.");
            return;
        }
        if (!detailsText.trim()) {
            setError("Bitte eine Beschreibung eintragen.");
            return;
        }
        if (systems.length === 0) {
            setError("Bitte mindestens ein System auswählen.");
            return;
        }

        setSending(true);
        try {
            const form = new FormData();
            form.append("type", type);
            systems.forEach((s) => form.append("systems[]", s));
            form.append("goal", goal.trim());
            form.append("details_html", detailsHtml);
            form.append("details_text", detailsText);
            if (reference.trim()) {
                form.append("reference", reference.trim());
            }
            form.append("page_url", window.location.href);
            form.append("page_title", document.title);
            form.append("viewport", `${window.innerWidth}x${window.innerHeight}`);

            let visibilityScope = "private";
            let visibilityDepartmentIds = [];
            if (visibilityChoice === "all") {
                visibilityScope = "all";
            } else if (visibilityChoice === "departments_all") {
                visibilityScope = "departments";
                visibilityDepartmentIds = departments.map((department) => department.id);
            } else if (visibilityChoice.startsWith("department:")) {
                visibilityScope = "departments";
                visibilityDepartmentIds = [Number(visibilityChoice.slice("department:".length))];
            }

            form.append("visibility_scope", visibilityScope);
            visibilityDepartmentIds.forEach((id) => form.append("visibility_department_ids[]", String(id)));
            form.append("meeting_visible", meetingVisible ? "1" : "0");
            attachments.forEach((file) => form.append("attachments[]", file));

            const {data} = await axios.post("/support/tickets", form);
            setSuccessId(data.external_id);
        } catch (e) {
            const message =
                e.response?.data?.message ||
                Object.values(e.response?.data?.errors || {})[0]?.[0] ||
                "Das Ticket konnte nicht gesendet werden. Bitte noch einmal versuchen.";
            setError(message);
        } finally {
            setSending(false);
        }
    };

    const editorButton = (label, action, isActive) => (
        <Button
            size="sm"
            variant={isActive ? "secondary" : "outline-secondary"}
            className="px-2 py-0"
            onMouseDown={(e) => {
                e.preventDefault();
                action();
            }}
        >
            {label}
        </Button>
    );

    return (
        <Modal show={show} onHide={close} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title className="fs-5">
                    {successId
                        ? "Ticket gesendet"
                        : type === null
                          ? "Support"
                          : TITEL[type]}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body onPaste={handlePaste}>
                {successId ? (
                    <div className="text-center py-4">
                        <div className="fs-1 mb-2">✓</div>
                        <p className="mb-1">Danke, {auth?.name?.split(" ")[0]}! Dein Ticket ist angekommen.</p>
                        <p className="text-muted small mb-0">Kennung: {successId}</p>
                    </div>
                ) : type === null ? (
                    <div className="d-flex flex-column flex-sm-row gap-3 py-3">
                        {/* Die beiden Einstiege - bewusst große Flächen statt
                            kleiner Radio-Buttons. */}
                        <button
                            type="button"
                            className="btn btn-outline-danger flex-fill py-4"
                            onClick={() => setType("problem")}
                        >
                            <div className="fs-4 mb-1">⚠</div>
                            <div className="fw-semibold">Problem melden</div>
                            <div className="small text-muted">Etwas funktioniert nicht wie erwartet</div>
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary flex-fill py-4"
                            onClick={() => setType("vorschlag")}
                        >
                            <div className="fs-4 mb-1">💡</div>
                            <div className="fw-semibold">Verbesserungsvorschlag</div>
                            <div className="small text-muted">Eine Idee, wie etwas besser laufen könnte</div>
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary flex-fill py-4"
                            onClick={() => setType("hinweis")}
                        >
                            <div className="fs-4 mb-1">💬</div>
                            <div className="fw-semibold">Hinweis / Frage</div>
                            <div className="small text-muted">Alles andere - eine Frage oder eine Information</div>
                        </button>
                    </div>
                ) : (
                    <Form onSubmit={(e) => e.preventDefault()}>
                        <div className="mb-3">
                            <Form.Label className="text-muted small text-uppercase mb-1" style={{letterSpacing: "0.04em"}}>Betrifft</Form.Label>
                            <div className="d-flex flex-wrap gap-2">
                                {SYSTEMS.map(({key, label}) => {
                                    const active = systems.includes(key);
                                    return (
                                        <Button
                                            key={key}
                                            size="sm"
                                            variant={active ? "primary" : "outline-secondary"}
                                            className="rounded-pill"
                                            onClick={() => toggleSystem(key)}
                                            aria-pressed={active}
                                        >
                                            {label}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold mb-1">Betreff</Form.Label>
                            <Form.Control
                                value={goal}
                                maxLength={500}
                                placeholder={copy.goalPlaceholder}
                                onChange={(e) => setGoal(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold mb-1">Beschreibung</Form.Label>
                            <div className="border rounded">
                                <div className="d-flex gap-1 border-bottom p-1">
                                    {editorButton(<strong>F</strong>, () => editor.chain().focus().toggleBold().run(), editor?.isActive("bold"))}
                                    {editorButton(<em>K</em>, () => editor.chain().focus().toggleItalic().run(), editor?.isActive("italic"))}
                                    {editorButton(<u>U</u>, () => editor.chain().focus().toggleUnderline().run(), editor?.isActive("underline"))}
                                    {editorButton("• Liste", () => editor.chain().focus().toggleBulletList().run(), editor?.isActive("bulletList"))}
                                    {editorButton("1. Liste", () => editor.chain().focus().toggleOrderedList().run(), editor?.isActive("orderedList"))}
                                </div>
                                <EditorContent
                                    editor={editor}
                                    className="p-2 support-editor"
                                    style={{minHeight: "140px"}}
                                />
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Control
                                size="sm"
                                value={reference}
                                maxLength={200}
                                placeholder={copy.referencePlaceholder}
                                onChange={(e) => setReference(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <div className="d-flex align-items-center gap-1 mb-2">
                                <Form.Label className="fw-semibold mb-0">Sichtbarkeit</Form.Label>
                                <HelpTooltip id="ticket-visibility-help" placement="top">
                                    Lege fest, wer dieses Ticket zusätzlich zu dir sehen darf. Ohne Auswahl bleibt es nur für dich sichtbar.
                                </HelpTooltip>
                            </div>

                            <div className="border rounded p-2 d-grid gap-2">
                                <Form.Check
                                    type="radio"
                                    id="ticket-visibility-private"
                                    name="ticket-visibility"
                                    label="Nur für mich"
                                    checked={visibilityChoice === "private"}
                                    onChange={() => setVisibilityChoice("private")}
                                />

                                {departments.map((department) => {
                                    const members = department.members?.length
                                        ? department.members.join(", ")
                                        : "Der Abteilung sind aktuell keine aktiven Benutzer zugeordnet.";
                                    return (
                                        <div key={department.id} className="d-flex align-items-center gap-1">
                                            <Form.Check
                                                type="radio"
                                                id={`ticket-visibility-department-${department.id}`}
                                                name="ticket-visibility"
                                                label={`Abteilung ${department.name}`}
                                                checked={visibilityChoice === `department:${department.id}`}
                                                onChange={() => setVisibilityChoice(`department:${department.id}`)}
                                            />
                                            <HelpTooltip
                                                id={`ticket-visibility-department-${department.id}-members`}
                                                placement="top"
                                                ariaLabel={`Mitglieder der Abteilung ${department.name}`}
                                            >
                                                {members}
                                            </HelpTooltip>
                                        </div>
                                    );
                                })}

                                {departments.length > 1 && (
                                    <div className="d-flex align-items-center gap-1">
                                        <Form.Check
                                            type="radio"
                                            id="ticket-visibility-all-departments"
                                            name="ticket-visibility"
                                            label="Alle meine Abteilungen"
                                            checked={visibilityChoice === "departments_all"}
                                            onChange={() => setVisibilityChoice("departments_all")}
                                        />
                                        <HelpTooltip
                                            id="ticket-visibility-all-departments-members"
                                            placement="top"
                                            ariaLabel="Mitglieder aller eigenen Abteilungen"
                                        >
                                            {departments.map((department) => `${department.name}: ${(department.members ?? []).join(", ") || "keine aktiven Benutzer"}`).join(" · ")}
                                        </HelpTooltip>
                                    </div>
                                )}

                                <Form.Check
                                    type="radio"
                                    id="ticket-visibility-all"
                                    name="ticket-visibility"
                                    label="Alle Benutzer"
                                    checked={visibilityChoice === "all"}
                                    onChange={() => setVisibilityChoice("all")}
                                />

                                <div className="border-top pt-2 d-flex align-items-center gap-1">
                                    <Form.Check
                                        type="switch"
                                        id="ticket-meeting-visible"
                                        label={`Auch für ${supportTicketVisibilityOptions.meeting_user_label ?? "Meeting-Benutzer"} sichtbar`}
                                        checked={meetingVisible}
                                        onChange={(event) => setMeetingVisible(event.target.checked)}
                                    />
                                    <HelpTooltip id="ticket-meeting-visible-help" placement="top">
                                        Diese Freigabe ist unabhängig von der Auswahl darüber und macht das Ticket gezielt im Meeting-Konto sichtbar.
                                    </HelpTooltip>
                                </div>
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-2">
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                                {previews.map(({file, url}, i) => (
                                    <div key={url} className="position-relative">
                                        <img
                                            src={url}
                                            alt={file.name}
                                            style={{height: 64, width: 64, objectFit: "cover"}}
                                            className="rounded border"
                                        />
                                        <Button
                                            size="sm"
                                            variant="danger"
                                            className="position-absolute top-0 end-0 py-0 px-1"
                                            style={{transform: "translate(30%, -30%)", lineHeight: 1.2}}
                                            aria-label={`${file.name} entfernen`}
                                            onClick={() =>
                                                setAttachments((prev) => prev.filter((_, j) => j !== i))
                                            }
                                        >
                                            ×
                                        </Button>
                                    </div>
                                ))}
                                {attachments.length < MAX_ATTACHMENTS && (
                                    <>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            + Screenshot
                                        </Button>
                                        <span className="text-muted small">oder mit Strg+V einfügen</span>
                                    </>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/gif,image/webp"
                                    multiple
                                    hidden
                                    onChange={(e) => {
                                        addFiles([...e.target.files]);
                                        e.target.value = "";
                                    }}
                                />
                            </div>
                        </Form.Group>

                        {error && (
                            <Alert variant="danger" className="py-2 mb-2">
                                {error}
                            </Alert>
                        )}
                    </Form>
                )}
            </Modal.Body>

            {!successId && type !== null && (
                <Modal.Footer className="justify-content-between">
                    <Button variant="link" className="text-muted ps-0" onClick={() => setType(null)}>
                        ‹ Zurück
                    </Button>
                    <Button variant="primary" disabled={sending} onClick={submit}>
                        {sending ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Wird gesendet…
                            </>
                        ) : (
                            "Ticket senden"
                        )}
                    </Button>
                </Modal.Footer>
            )}
            {successId && (
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={reset}>
                        Weiteres Ticket
                    </Button>
                    <Button variant="primary" onClick={close}>
                        Schließen
                    </Button>
                </Modal.Footer>
            )}
        </Modal>
    );
}
